// content-block.service.ts

import { Injectable } from '@angular/core';
import {
  BlockContent,
  BlockSlider,
  Href,
  HrefUrls,
  SliderBlockContent,
  SliderSettings,
  SliderSettingsAnimation,
  StructureContent,
  UploadPhoto,
  VERSION_TYPE,
} from '../pages/models/content.model';
import { ContentStorageService } from './content-storage.service';

/**
 * Serwis odpowiedzialny za operacje na strukturach blokowych w pamięci lokalnej.
 * Zapewnia wyszukiwanie, podmianę, usuwanie oraz aktualizację bloków i ich powiązań w strukturze.
 */
@Injectable({
  providedIn: 'root',
})
export class ContentBlockService {
  private currentStructureId = '';

  constructor(private storageService: ContentStorageService) { }

  /**
   * Wyszukuje blok o podanym identyfikatorze w przekazanej strukturze lub we wszystkich lokalnych strukturach.
   * @param blockId - Identyfikator szukanego bloku.
   * @param structure - Opcjonalnie konkretna struktura lub tablica struktur do przeszukania.
   * @returns Znaleziony blok lub null.
   */
  getBlockByIdFromStructure(
    blockId: string,
    structure?: StructureContent | StructureContent[] | null
  ): BlockContent | null {
    // Pobierz wszystkie struktury block_ z localStorage, jeśli nie przekazano konkretnej struktury
    let structures: StructureContent[] = [];
    if (!structure) {
      structures = this.storageService.getAllBlockStructuresFromLocalStorage(
        VERSION_TYPE.WORKING
      );
    } else if (Array.isArray(structure)) {
      structures = structure;
    } else {
      structures = [structure];
    }

    // Przeszukaj każdą strukturę
    for (const struct of structures) {
      if (!struct || !Array.isArray(struct.structure)) continue;
      const found = this.findBlockInStructure(struct.structure, blockId);
      if (found) {
        this.currentStructureId = struct.id;
        return found;
      }
    }
    return null;
  }

  /**
   * Przeszukuje rekurencyjnie strukturę bloków w poszukiwaniu bloku o podanym ID.
   * @param structure - Tablica bloków do przeszukania.
   * @param blockId - Identyfikator szukanego bloku.
   * @returns Znaleziony blok lub null.
   */
  findBlockInStructure(
    structure: BlockContent[],
    blockId: string
  ): BlockContent | null {
    const stack = [...structure];
    // Wyodrębnienie czystego ID z formatu block_<id> jeśli istnieje
    const extractedId = this.extractIdFromBlockString(blockId);

    while (stack.length) {
      const block = stack.pop()!;

      // Standardowe porównanie u_id
      if (block.u_id && String(block.u_id) === String(blockId)) {
        return block;
      }

      // Dodatkowa logika dla bloków z null u_id
      if (!block.u_id && extractedId) {
        // Sprawdzenie czy block.id odpowiada wyodrębnionemu ID
        if (block.id && String(block.id) === extractedId) {
          return block;
        }

        // Sprawdzenie innych możliwych pól identyfikujących
        if (block.anchor && String(block.anchor) === extractedId) {
          return block;
        }
      }

      // Kontynuacja rekurencyjnego przeszukiwania
      if (block.children?.length) {
        stack.push(...block.children.reverse());
      }

      if (block.special_block) {
        const specialBlocks = Array.isArray(block.special_block)
          ? [...block.special_block]
          : [block.special_block];
        stack.push(...specialBlocks.reverse());
      }
    }

    return null;
  }

  private extractIdFromBlockString(blockId: string): string | null {
    if (blockId.startsWith('block_')) {
      return blockId.substring(6); // Usuwa prefix 'block_'
    }
    return null;
  }

  /**
   * Podmienia blok w strukturze na nowy blok o podanym UID.
   * @param blockUID - Unikalny identyfikator bloku do podmiany.
   * @param newBlockContent - Nowa zawartość bloku.
   * @param versionType - Typ wersji (domyślnie WORKING).
   * @returns true, jeśli podmiana się powiodła.
   */
  replaceBlockInStructure(
    blockUID: string,
    newBlockContent: BlockContent,
    versionType: VERSION_TYPE = VERSION_TYPE.WORKING
  ): boolean {
    console.log('[ContentBlockService->replaceBlockInStructure] newBlockContent ', newBlockContent);
    const structures =
      this.storageService.getAllBlockStructuresFromLocalStorage(versionType);
    if (!structures || structures.length === 0) {
      return false;
    }
    let blockReplaced = false;
    for (const structure of structures) {
      if (!structure || !Array.isArray(structure.structure)) continue;
      console.log('[ContentBlockService->replaceBlockInStructure] oldBlockContent ', structure.structure);
      const replaced = this.replaceBlockInStructureRecursive(
        structure.structure,
        blockUID,
        newBlockContent
      );
      if (replaced) {
        blockReplaced = true;
        const storageKey = `block_${structure.id}_${versionType}`;
        localStorage.setItem(storageKey, JSON.stringify(structure));
      }
    }
    return blockReplaced;
  }

  /**
   * Pomocnicza metoda rekurencyjna do podmiany bloku w strukturze.
   * @param blocks - Tablica bloków do przeszukania.
   * @param blockUID - Unikalny identyfikator bloku do podmiany.
   * @param newBlockContent - Nowa zawartość bloku.
   * @returns true, jeśli podmiana się powiodła.
   */
  private replaceBlockInStructureRecursive(
    blocks: BlockContent[],
    blockUID: string,
    newBlockContent: BlockContent
  ): boolean {
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      if (block.u_id === blockUID) {
        blocks[i] = { ...newBlockContent, id: blockUID };
        return true;
      }
      if (block.children?.length) {
        const replacedInChildren = this.replaceBlockInStructureRecursive(
          block.children,
          blockUID,
          newBlockContent
        );
        if (replacedInChildren) return true;
      }
      if (block.special_block) {
        const specialBlocks = Array.isArray(block.special_block)
          ? block.special_block
          : [block.special_block];
        const replacedInSpecial = this.replaceBlockInStructureRecursive(
          specialBlocks,
          blockUID,
          newBlockContent
        );
        if (replacedInSpecial) {
          block.special_block = Array.isArray(block.special_block)
            ? specialBlocks
            : specialBlocks[0];
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Usuwa blok o podanym UID ze wszystkich struktur.
   * @param blockUID - Unikalny identyfikator bloku do usunięcia.
   * @param versionType - Typ wersji (domyślnie WORKING).
   * @returns true, jeśli blok został usunięty.
   */
  deleteBlockFromStructure(
    blockUID: string,
    versionType: VERSION_TYPE = VERSION_TYPE.WORKING
  ): boolean {
    const structures =
      this.storageService.getAllBlockStructuresFromLocalStorage(versionType);
    let blockDeleted = false;
    for (const structure of structures) {
      if (!structure?.structure) continue;
      const initialLength = structure.structure.length;
      const newStructure = this.deleteBlockRecursive(
        structure.structure,
        blockUID
      );
      if (newStructure.length !== initialLength) {
        structure.structure = newStructure;
        const storageKey = `block_${structure.id}_${versionType}`;
        localStorage.setItem(storageKey, JSON.stringify(structure));
        blockDeleted = true;
      }
    }
    return blockDeleted;
  }

  /**
   * Pomocnicza metoda rekurencyjna do usuwania bloku z tablicy bloków.
   * @param blocks - Tablica bloków do przeszukania.
   * @param blockUID - Unikalny identyfikator bloku do usunięcia.
   * @returns Nowa tablica bloków bez usuniętego bloku.
   */
  private deleteBlockRecursive(
    blocks: BlockContent[],
    blockUID: string
  ): BlockContent[] {
    return blocks.filter((block) => {
      if (block.u_id === blockUID) return false;
      if (block.children?.length) {
        block.children = this.deleteBlockRecursive(block.children, blockUID);
      }
      if (block.special_block) {
        const originalIsArray = Array.isArray(block.special_block);
        let specialBlocks = originalIsArray
          ? [...block.special_block]
          : [block.special_block];
        specialBlocks = this.deleteBlockRecursive(specialBlocks, blockUID);
        block.special_block = originalIsArray
          ? specialBlocks
          : specialBlocks[0] || undefined;
      }
      return true;
    });
  }

  /**
   * Aktualizuje identyfikatory mediów w strukturach bloków.
   * @param old_media_id - Stary identyfikator medium.
   * @param new_media_id - Nowy identyfikator medium.
   * @returns true, jeśli dokonano jakichkolwiek zmian.
   */
  updateMediaIds(old_media_id: number, new_media_id: number): boolean {
    const structures =
      this.storageService.getAllBlockStructuresFromLocalStorage(
        VERSION_TYPE.WORKING
      );
    if (!structures || structures.length === 0) {
      return false;
    }
    let madeChanges = false;
    for (const structure of structures) {
      if (!structure || !Array.isArray(structure.structure)) continue;
      const changedInStructure = this.updateMediaIdsInStructure(
        structure.structure,
        old_media_id,
        new_media_id
      );
      if (changedInStructure) {
        madeChanges = true;
        const storageKey = `block_${structure.id}_${VERSION_TYPE.WORKING}`;
        localStorage.setItem(storageKey, JSON.stringify(structure));
      }
    }
    return madeChanges;
  }

  /**
   * Zarządza elementami slidera - aktualizuje istniejące, usuwa niepotrzebne, dodaje nowe
   * @param sliders - Lista docelowych elementów slidera
   * @param sliderBlockId - Identyfikator głównego bloku slidera
   * @returns true jeśli wszystkie operacje się powiodły
   * @remarks Wykonuje pełną synchronizację elementów slidera ze strukturą
   */
  setSliderItems(sliders: BlockSlider[], sliderBlockId: string): boolean {
    console.log('[ContentBlockService->setSliderItems] Rozpoczęcie synchronizacji slidera');
    console.log('[ContentBlockService->setSliderItems] sliders:', sliders);
    console.log('[ContentBlockService->setSliderItems] sliderBlockId:', sliderBlockId);

    // Pobranie głównego bloku slidera
    const sliderBlock = this.getSliderBlock(sliderBlockId);
    if (!sliderBlock || !sliderBlock.special_block) {
      console.error('[ContentBlockService->setSliderItems] Nie znaleziono bloku slidera o ID:', sliderBlockId);
      return false;
    }

    console.log('[ContentBlockService->setSliderItems] Znaleziony blok slidera:', sliderBlock);

    // Pobranie kontenera ze slajdami
    const slidesContainer = this.findSlidesContainer(sliderBlock.special_block.structure);
    const slidesChildrenContainer = this.findSlidesContainer(sliderBlock.special_block.children);
    console.log('[ContentBlockService->setSliderItems] Znaleziony blok slidera children:', slidesChildrenContainer);
    if (!slidesContainer || !slidesChildrenContainer) {
      console.error('[ContentBlockService->setSliderItems] Nie znaleziono kontenera slajdów w strukturze slidera');
      return false;
    }

    console.log('[ContentBlockService->setSliderItems] Znaleziony kontener slajdów:', slidesContainer);
    console.log('[ContentBlockService->setSliderItems] Aktualne dzieci kontenera:', slidesContainer.children);

    // Synchronizacja elementów
    try {
      this.synchronizeSliderElements(slidesContainer, sliders);
      this.synchronizeSliderElements(slidesChildrenContainer, sliders);

      // Zapisanie zmodyfikowanego bloku slidera
      const saveResult = this.replaceBlockInStructure(sliderBlockId, sliderBlock);
      console.log('[ContentBlockService->setSliderItems] Wynik zapisu bloku slidera:', saveResult);

      return saveResult;
    } catch (error) {
      console.error('[ContentBlockService->setSliderItems] Błąd podczas synchronizacji:', error);
      return false;
    }
  }

  /**
   * Wyszukuje kontener zawierający slajdy w strukturze slidera
   * @param structure - Struktura bloku slidera do przeszukania
   * @returns Blok kontenera slajdów lub null jeśli nie znaleziono
   * @remarks Przeszukuje rekurencyjnie strukturę w poszukiwaniu elementu z segment_type: "slides"
   */
  private findSlidesContainer(structure: BlockContent[]): BlockContent | null {
    console.log('[ContentBlockService->findSlidesContainer] Przeszukiwanie struktury:', structure);

    const stack = [...structure];

    while (stack.length > 0) {
      const block = stack.pop()!;
      console.log('[ContentBlockService->findSlidesContainer] Sprawdzanie bloku:', block.u_id, block.settings);

      // Sprawdzenie czy to kontener slajdów
      if (block.settings?.segment_type === 'slides') {
        console.log('[ContentBlockService->findSlidesContainer] Znaleziono kontener slajdów:', block.u_id, block);
        return block;
      }

      // Dodanie dzieci do przeszukania
      if (block.children && Array.isArray(block.children)) {
        stack.push(...block.children.reverse());
      }
    }

    console.warn('[ContentBlockService->findSlidesContainer] Nie znaleziono kontenera slajdów');
    return null;
  }


  /**
   * Wykonuje synchronizację elementów slidera z docelową listą
   * @param slidesContainer - Kontener zawierający slajdy
   * @param targetSliders - Docelowa lista slajdów
   * @remarks Aktualizuje, usuwa i dodaje elementy zgodnie z listą docelową
   */
  private synchronizeSliderElements(slidesContainer: BlockContent, targetSliders: BlockSlider[]): void {
    console.log('[ContentBlockService->synchronizeSliderElements] Rozpoczęcie synchronizacji');
    console.log('[ContentBlockService->synchronizeSliderElements] Aktualne dzieci:', slidesContainer.children);
    console.log('[ContentBlockService->synchronizeSliderElements] Docelowe slajdy:', targetSliders);

    const currentChildren = slidesContainer.children || [];
    const targetUIds = targetSliders.map(slider => slider.uId);
    const currentUIds = currentChildren.map(child => child.u_id).filter(Boolean);

    console.log('[ContentBlockService->synchronizeSliderElements] Docelowe uIds:', targetUIds);
    console.log('[ContentBlockService->synchronizeSliderElements] Aktualne uIds:', currentUIds);

    // 1. Aktualizacja istniejących elementów
    console.log('[ContentBlockService->synchronizeSliderElements] Krok 1: Aktualizacja istniejących elementów');
    targetSliders.forEach(slider => {
      const existingElement = currentChildren.find(child => child.u_id === slider.uId);
      if (existingElement) {
        console.log('[ContentBlockService->synchronizeSliderElements] Aktualizacja elementu:', slider.uId);
        this.updateSliderElementStyles(existingElement, slider);
      }
    });


    // 2. Dodanie nowych elementów
    console.log('[ContentBlockService->synchronizeSliderElements] Krok 3: Dodawanie nowych elementów');
    const templateElement = this.findTemplateForNewSlides(slidesContainer.children);
    console.log('[ContentBlockService->synchronizeSliderElements] Templatka', templateElement);
    targetSliders.forEach(slider => {
      const existsInStructure = slidesContainer.children.some(child => child.u_id === slider.uId);
      if (!existsInStructure) {
        console.log('[ContentBlockService->synchronizeSliderElements] Tworzenie nowego elementu:', slider.uId);
        const newElement = this.createSliderElement(templateElement, slider);
        if (newElement) {
          slidesContainer.children.push(newElement);
        }
      }
    });

    // 3. Usunięcie elementów nieobecnych w docelowej liście
    console.log('[ContentBlockService->synchronizeSliderElements] Krok 2: Usuwanie niepotrzebnych elementów');
    slidesContainer.children = currentChildren.filter(child => {
      const shouldKeep = !child.u_id || targetUIds.includes(child.u_id);
      if (!shouldKeep) {
        console.log('[ContentBlockService->synchronizeSliderElements] Usuwanie elementu:', child.u_id);
      }
      return shouldKeep;
    });

    // 4. Aktualizacja numeracji custom_order
    console.log('[ContentBlockService->synchronizeSliderElements] Krok 4: Aktualizacja numeracji');
    this.updateCustomOrderBySliderSequence(slidesContainer.children, targetSliders);

    console.log('[ContentBlockService->synchronizeSliderElements] Finalna struktura dzieci:', slidesContainer.children);
  }

  /**
   * Aktualizuje style elementu slidera na podstawie danych z BlockSlider
   * @param element - Element struktury do aktualizacji
   * @param sliderData - Dane slajdu z nowymi stylami
   * @remarks Modyfikuje background_color i background_image w stylach elementu
   */
  private updateSliderElementStyles(element: BlockContent, sliderData: BlockSlider): void {
    console.log('[ContentBlockService->updateSliderElementStyles] Aktualizacja stylów dla:', element.u_id);
    console.log('[ContentBlockService->updateSliderElementStyles] Nowe dane:', sliderData);

    // Inicjalizacja struktury stylów jeśli nie istnieje
    if (!element.styles) {
      element.styles = {};
    }
    if (!element.styles.all) {
      element.styles.all = {};
    }
    if (!element.styles.all.basic) {
      element.styles.all.basic = {};
    }

    // Aktualizacja background_color
    if (sliderData.backgroundColor) {
      console.log('[ContentBlockService->updateSliderElementStyles] Ustawianie backgroundColor:', sliderData.backgroundColor);
      element.styles.all.basic.background_color = sliderData.backgroundColor;
    }

    // Aktualizacja background_image  
    if (sliderData.backgroundImage) {
      console.log('[ContentBlockService->updateSliderElementStyles] Ustawianie backgroundImage:', sliderData.backgroundImage);
      element.styles.all.basic.background_image = sliderData.backgroundImage;
    } else {
      // Usunięcie background_image jeśli nie podano
      if (element.styles.all.basic.background_image) {
        console.log('[ContentBlockService->updateSliderElementStyles] Usuwanie backgroundImage');
        delete element.styles.all.basic.background_image;
      }
    }

    console.log('[ContentBlockService->updateSliderElementStyles] Zaktualizowane style:', element.styles.all.basic);
  }

  /**
   * Znajduje i przygotowuje szablon dla nowych slajdów tworząc nową instancję SliderBlockContent
   * @param children - Lista istniejących elementów kontenera
   * @returns Nowy obiekt SliderBlockContent lub null
   * @remarks Kopiuje wszystkie pola z wyjątkiem: children, content, media, styles i innych wykluczonych
   */
  private findTemplateForNewSlides(children: BlockContent[]): SliderBlockContent | null {
    console.log('[ContentBlockService->findTemplateForNewSlides] Szukanie szablonu w:', children);

    if (!children || children.length === 0) {
      console.warn('[ContentBlockService->findTemplateForNewSlides] Brak elementów do użycia jako szablon');
      return null;
    }

    // Znajdź element z najniższym custom_order jako szablon
    const template = children.reduce((prev, current) =>
      (current.custom_order < prev.custom_order) ? current : prev
    );

    console.log('[ContentBlockService->findTemplateForNewSlides] Wybrany szablon:', template.u_id, 'custom_order:', template.custom_order);

    try {
      // Głębokie kopiowanie struktury
      const clonedTemplate = typeof structuredClone === 'function'
        ? structuredClone(template)
        : JSON.parse(JSON.stringify(template));

      // Destrukturyzacja z pominięciem wykluczonych pól
      const {
        children: _,
        content: __,
        media,
        media_alt,
        media_id,
        media_ids,
        medias,
        styles,
        translated_fields,
        translations,
        ...templateCore
      } = clonedTemplate;

      // Budowa nowego obiektu SliderBlockContent
      const newSliderBlock: SliderBlockContent = {
        ...templateCore,
        children: [],
        content: null,
        media: null,
        media_alt: null,
        media_id: null,
        media_ids: [],
        medias: null,
        styles: { all: { basic: {} } },
        translated_fields: [],
        translations: []
      };

      console.log('[ContentBlockService->findTemplateForNewSlides] Wygenerowany SliderBlockContent:', newSliderBlock);
      return newSliderBlock;

    } catch (error) {
      console.error('[ContentBlockService->findTemplateForNewSlides] Błąd przetwarzania szablonu:', error);
      return null;
    }
  }


  /**
   * Tworzy nowy element slidera na podstawie szablonu
   * @param template - Element szablon do skopiowania
   * @param sliderData - Dane nowego slajdu
   * @returns Nowy element slidera lub null w przypadku błędu
   * @remarks Kopiuje strukturę szablonu i aktualizuje u_id oraz style
   */
  private createSliderElement(template: BlockContent | null, sliderData: BlockSlider): BlockContent | null {
    console.log('[ContentBlockService->createSliderElement] Tworzenie elementu dla:', sliderData.uId);

    if (!template) {
      console.error('[ContentBlockService->createSliderElement] Brak szablonu do kopiowania');
      return null;
    }

    try {
      // Głębokie kopiowanie szablonu
      const newElement: BlockContent = typeof structuredClone === 'function'
        ? structuredClone(template)
        : JSON.parse(JSON.stringify(template));

      console.log('[ContentBlockService->createSliderElement] Skopiowany szablon:', newElement);

      // Aktualizacja identyfikatora
      newElement.u_id = sliderData.uId;
      newElement.id = null; // Nowy element nie ma jeszcze ID z bazy
      newElement.selected = false;
      newElement.is_active = sliderData.isActive;

      // Aktualizacja stylów
      this.updateSliderElementStyles(newElement, sliderData);

      console.log('[ContentBlockService->createSliderElement] Utworzony element:', newElement);
      return newElement;

    } catch (error) {
      console.error('[ContentBlockService->createSliderElement] Błąd podczas kopiowania szablonu:', error);
      return null;
    }
  }

  /**
   * Aktualizuje numerację custom_order elementów zgodnie z kolejnością w tablicy sliderów
   * @param children - Lista elementów do ponumerowania
   * @param slidersOrder - Lista sliderów określająca kolejność
   * @remarks Numeruje elementy od 0 zgodnie z kolejnością w slidersOrder
   */
  private updateCustomOrderBySliderSequence(children: BlockContent[], slidersOrder: BlockSlider[]): void {
    console.log('[ContentBlockService->updateCustomOrderBySliderSequence] Aktualizacja numeracji');
    console.log('[ContentBlockService->updateCustomOrderBySliderSequence] Kolejność sliderów:', slidersOrder.map(s => s.uId));

    // Mapa uId -> pozycja w docelowej kolejności
    const orderMap = new Map<string, number>();
    slidersOrder.forEach((slider, index) => {
      orderMap.set(slider.uId, index);
    });

    console.log('[ContentBlockService->updateCustomOrderBySliderSequence] Mapa kolejności:', orderMap);

    // Aktualizacja custom_order dla każdego elementu
    children.forEach(child => {
      if (child.u_id && orderMap.has(child.u_id)) {
        const newOrder = orderMap.get(child.u_id)!;
        console.log('[ContentBlockService->updateCustomOrderBySliderSequence] Element:', child.u_id, 'stary order:', child.custom_order, 'nowy order:', newOrder);
        child.custom_order = newOrder;
      }
    });

    // Sortowanie dzieci według custom_order dla spójności
    children.sort((a, b) => (a.custom_order || 0) - (b.custom_order || 0));

    console.log('[ContentBlockService->updateCustomOrderBySliderSequence] Finalna kolejność:',
      children.map(child => ({ uId: child.u_id, order: child.custom_order }))
    );
  }


  /**
   * Pomocnicza metoda rekurencyjna do aktualizacji ID mediów w blokach.
   * @param blocks - Tablica bloków do przeszukania.
   * @param old_media_id - Stary identyfikator medium.
   * @param new_media_id - Nowy identyfikator medium.
   * @returns true, jeśli dokonano zmian.
   */
  private updateMediaIdsInStructure(
    blocks: BlockContent[],
    old_media_id: number,
    new_media_id: number
  ): boolean {
    if (!blocks || !Array.isArray(blocks)) return false;
    let madeChanges = false;
    for (const block of blocks) {
      if (block.media_ids && Array.isArray(block.media_ids)) {
        let hasChanges = false;
        block.media_ids = block.media_ids.map((id) => {
          if (id === old_media_id) {
            hasChanges = true;
            return new_media_id;
          }
          return id;
        });
        if (hasChanges) {
          madeChanges = true;
        }
      }
      if (block.children && Array.isArray(block.children)) {
        const changedInChildren = this.updateMediaIdsInStructure(
          block.children,
          old_media_id,
          new_media_id
        );
        if (changedInChildren) madeChanges = true;
      }
      if (block.special_block) {
        const specialBlocks = Array.isArray(block.special_block)
          ? block.special_block
          : [block.special_block];
        const changedInSpecial = this.updateMediaIdsInStructure(
          specialBlocks,
          old_media_id,
          new_media_id
        );
        if (changedInSpecial) {
          madeChanges = true;
          block.special_block = Array.isArray(block.special_block)
            ? specialBlocks
            : specialBlocks[0];
        }
      }
    }
    return madeChanges;
  }

  /**
   * Zwraca identyfikator ostatnio przetwarzanej struktury (np. podczas wyszukiwania bloku).
   * @returns ID aktualnej struktury jako string.
   */
  getCurrentStructureId(): string {
    return this.currentStructureId;
  }

  /**
   * Wyszukuje blok o określonym u_id i special_block_type = 1 (slider)
   * @param blockId - Identyfikator szukanego bloku.
   * @param structure - Opcjonalnie konkretna struktura lub tablica struktur do przeszukania.
   * @returns Znaleziony blok-slider lub null.
   */
  getSliderBlock(
    blockId: string,
    structure?: StructureContent | StructureContent[] | null
  ): BlockContent | null {
    // Pobierz wszystkie struktury block_ z localStorage, jeśli nie przekazano konkretnej struktury
    let structures: StructureContent[] = [];
    if (!structure) {
      structures = this.storageService.getAllBlockStructuresFromLocalStorage(
        VERSION_TYPE.WORKING
      );
    } else if (Array.isArray(structure)) {
      structures = structure;
    } else {
      structures = [structure];
    }

    // Przeszukaj każdą strukturę
    for (const struct of structures) {
      if (!struct || !Array.isArray(struct.structure)) continue;
      const found = this.findSliderBlockInStructure(struct.structure, blockId);
      if (found) {
        console.log('[ContentBlockService->getSliderBlock] slider ', found);
        this.currentStructureId = struct.id;
        return found;
      }
    }
    return null;
  }
  /**
   * Przeszukuje rekurencyjnie strukturę bloków w poszukiwaniu bloku-slidera o podanym ID.
   * @param structure - Tablica bloków do przeszukania.
   * @param blockId - Identyfikator szukanego bloku.
   * @returns Znaleziony blok-slider lub null.
   */
  private findSliderBlockInStructure(
    structure: BlockContent[],
    blockId: string
  ): BlockContent | null {
    const stack = [...structure];
    while (stack.length) {
      const block = stack.pop()!;
      if (
        String(block.u_id) === String(blockId)
        && block.special_block_type === 1
      ) {
        return block;
      }

      if (block.children?.length) {
        stack.push(...block.children.reverse());
      }

      if (block.special_block) {
        const specialBlocks = Array.isArray(block.special_block)
          ? [...block.special_block]
          : [block.special_block];
        stack.push(...specialBlocks.reverse());
      }
    }
    return null;
  }

  /**
   * Pobiera ustawienia slidera z bloku o podanym ID
   * @param blockId - Unikalny identyfikator bloku slidera
   * @returns Ustawienia slidera lub null jeśli blok nie istnieje lub nie jest sliderem
   */
  getSliderSettings(blockId: string): SliderSettings | null {
    const sliderBlock = this.getSliderBlock(blockId);
    if (
      !sliderBlock ||
      !sliderBlock.special_block ||
      !sliderBlock.special_block.settings
    ) {
      console.warn(
        `Nie znaleziono bloku slidera o ID: ${blockId} lub brak ustawień`
      );
      return null;
    }

    // Zwracamy kopię obiektu ustawień, aby uniknąć nieoczekiwanych zmian przez referencję
    return { ...sliderBlock.special_block.settings };
  }

  /**
   * Zapisuje ustawienia slidera do bloku o podanym ID
   * @param blockId - Unikalny identyfikator bloku slidera
   * @param settings - Nowe ustawienia slidera
   * @returns true jeśli zapis się powiódł, false w przeciwnym przypadku
   */
  setSliderSettings(
    blockId: string,
    settings: SliderSettingsAnimation
  ): boolean {
    const sliderBlock = this.getSliderBlock(blockId);
    if (!sliderBlock || !sliderBlock.special_block) {
      console.warn(`Nie znaleziono bloku slidera o ID: ${blockId}`);
      return false;
    }

    // Aktualizujemy ustawienia
    sliderBlock.special_block.settings = { ...settings };

    // Zapisujemy zmieniony blok w strukturze
    const replaced = this.replaceBlockInStructure(blockId, sliderBlock);
    if (!replaced) {
      console.error(
        `Nie udało się zapisać ustawień slidera dla bloku o ID: ${blockId}`
      );
    }

    return replaced;
  }

  /**
   * Aktualizuje zdjęcie w bloku oraz tworzy/aktualizuje element href z URL-ami dla wszystkich języków
   * @param photo - Dane zdjęcia do aktualizacji zawierające u_id, media_id, url i alt
   * @remarks Automatycznie zarządza href dla wszystkich języków dostępnych w translations bloku
   */
  setPhoto(photo: UploadPhoto): void {
    console.log('[ContentBlockService->setPhoto] Rozpoczęcie aktualizacji zdjęcia dla bloku:', photo.u_id);
    try {
      // 1. Znajdź blok w strukturze
      const blockToUpdate = this.getBlockByIdFromStructure(photo.u_id);
      if (!blockToUpdate) {
        console.error('[ContentBlockService->setPhoto] Blok nie znaleziony dla u_id:', photo.u_id);
        return;
      }

      console.log('[ContentBlockService->setPhoto] Znaleziono blok przed aktualizacją:', {
        u_id: blockToUpdate.u_id,
        media_id: blockToUpdate.media_id,
        media_alt: blockToUpdate.media_alt,
        media_ids: blockToUpdate.media_ids,
        href: blockToUpdate.href
      });

      // 2. Aktualizacja podstawowych pól mediów
      blockToUpdate.media_alt = photo.alt;

      if (photo.media_id) {
        blockToUpdate.media_id = photo.media_id;
        blockToUpdate.media_ids = [];

        // 3. Aktualizacja media_ids z walidacją
        const mediaIdNumber = parseInt(photo.media_id);
        if (!isNaN(mediaIdNumber)) {
          blockToUpdate.media_ids = blockToUpdate.media_ids || [];
          if (!blockToUpdate.media_ids.includes(mediaIdNumber)) {
            blockToUpdate.media_ids.push(mediaIdNumber);
            console.log('[ContentBlockService->setPhoto] Dodano media_id do tablicy:', mediaIdNumber);
          }
        } else {
          console.warn('[ContentBlockService->setPhoto] Nieprawidłowy format media_id:', photo.media_id);
        }
      }

      // 4. Pobierz aktualny język z sessionStorage
      const currentLanguage = sessionStorage.getItem('language') || 'pl';
      console.log('[ContentBlockService->setPhoto] Aktualny język z sessionStorage:', currentLanguage);

      // 5. Przygotuj listę języków z translations bloku
      const availableLanguages = this.getAvailableLanguagesFromBlock(blockToUpdate);
      console.log('[ContentBlockService->setPhoto] Dostępne języki z translations:', availableLanguages);

      // 6. Stwórz lub zaktualizuj element href
      this.createOrUpdateHref(blockToUpdate, photo.url, currentLanguage, availableLanguages);

      // 7. Zapisz zmiany przez replaceBlockInStructure
      const saveResult = this.replaceBlockInStructure(
        blockToUpdate.u_id,
        blockToUpdate
      );

      // 8. Dodatkowa walidacja i zapis przez ContentStorageService
      // if (saveResult) {
      //   console.log('[ContentBlockService->setPhoto] Pomyślnie zaktualizowano blok. Aktualizacja localStorage...');
      //   // Pobierz aktualną strukturę i zapisz przez ContentStorageService
      //   const currentStructure = this.storageService.getContent(
      //     `block_${this.getCurrentStructureId()}_working`
      //   );
      //   if (currentStructure) {
      //     this.storageService.saveBlockContent(currentStructure as StructureContent);
      //     console.log('[ContentBlockService->setPhoto] Struktura zaktualizowana w localStorage');
      //   }
      // } else {
      //   console.error('[ContentBlockService->setPhoto] Błąd podczas zapisywania bloku do struktury');
      // }

    } catch (error) {
      console.error('[ContentBlockService->setPhoto] Krytyczny błąd podczas aktualizacji zdjęcia:', error);
      throw error;
    }
  }

  /**
   * Pobiera listę dostępnych języków z elementu translations bloku
   * @param block - Blok treści z którego pobrać dostępne języki
   * @returns Tablica kodów języków lub ['pl'] jako fallback
   * @remarks Filtruje poprawne kody języków z tablicy translations
   */
  private getAvailableLanguagesFromBlock(block: BlockContent): string[] {
    console.log('[ContentBlockService->getAvailableLanguagesFromBlock] Analizowanie translations bloku:', block.u_id);
    console.log('[ContentBlockService->getAvailableLanguagesFromBlock] Zawartość translations:', block.translations);

    // Sprawdź czy translations istnieje i jest tablicą
    if (!block.translations || !Array.isArray(block.translations)) {
      console.warn('[ContentBlockService->getAvailableLanguagesFromBlock] Brak translations lub nieprawidłowy format, używam domyślnego języka pl');
      return ['pl'];
    }

    // Wyodrębnij kody języków z translations
    const languages = block.translations
      .map(translation => translation.locale)
      .filter(locale => locale && typeof locale === 'string' && locale.trim().length > 0);

    console.log('[ContentBlockService->getAvailableLanguagesFromBlock] Wyodrębnione języki:', languages);

    // Zwróć języki lub fallback do ['pl']
    const result = languages.length > 0 ? languages : ['pl'];
    console.log('[ContentBlockService->getAvailableLanguagesFromBlock] Finalna lista języków:', result);

    return result;
  }

  /**
   * Tworzy nowy lub aktualizuje istniejący element href w bloku zgodnie z interfejsem Href
   * @param block - Blok treści do aktualizacji
   * @param photoUrl - URL zdjęcia do przypisania dla aktualnego języka
   * @param currentLanguage - Aktualnie wybrany język z sessionStorage
   * @param availableLanguages - Lista wszystkich dostępnych języków z translations
   * @remarks URL jest przypisywany tylko dla aktualnego języka, pozostałe języki zachowują istniejące URL-e
   */
  private createOrUpdateHref(
    block: BlockContent,
    photoUrl: string | null,
    currentLanguage: string,
    availableLanguages: string[]
  ): void {
    console.log('[ContentBlockService->createOrUpdateHref] Rozpoczęcie zarządzania href dla bloku:', block.u_id);
    console.log('[ContentBlockService->createOrUpdateHref] Parametry:');
    console.log('  - photoUrl:', photoUrl);
    console.log('  - currentLanguage:', currentLanguage);
    console.log('  - availableLanguages:', availableLanguages);
    console.log('  - istniejący href:', block.href);

    // Przygotowanie struktury hrefUrls dla wszystkich dostępnych języków
    const hrefUrls: HrefUrls = {};

    availableLanguages.forEach(lang => {
      if (lang === currentLanguage && photoUrl) {
        // Przypisz URL tylko dla aktualnego języka
        hrefUrls[lang] = photoUrl;
        console.log(`[ContentBlockService->createOrUpdateHref] Przypisano nowy URL dla aktualnego języka ${lang}:`, photoUrl);
      } else {
        // Zachowaj istniejący URL dla innych języków lub ustaw pusty string
        const existingUrl = block.href?.hrefUrls?.[lang] || '';
        hrefUrls[lang] = existingUrl;
        console.log(`[ContentBlockService->createOrUpdateHref] Zachowano istniejący URL dla języka ${lang}:`, existingUrl || '(pusty)');
      }
    });

    console.log('[ContentBlockService->createOrUpdateHref] Przygotowana struktura hrefUrls:', hrefUrls);

    // Sprawdzenie czy href już istnieje w bloku
    if (block.href && typeof block.href === 'object') {
      console.log('[ContentBlockService->createOrUpdateHref] Aktualizacja istniejącego elementu href');

      // Aktualizuj istniejący href zachowując pozostałe właściwości
      block.href.hrefUrls = hrefUrls;

      console.log('[ContentBlockService->createOrUpdateHref] Zaktualizowany istniejący href:', block.href);
    } else {
      console.log('[ContentBlockService->createOrUpdateHref] Tworzenie nowego elementu href zgodnie z interfejsem Href');

      // Stwórz nowy href zgodnie z interfejsem Href
      const newHref: Href = {
        hrefType: 1,
        hrefTarget: 0,
        linkDescription: '',
        hrefUrls: hrefUrls,
        noFollow: false,
        anchor: null
      };

      // Przypisz nowy href do bloku
      block.href = newHref;

      console.log('[ContentBlockService->createOrUpdateHref] Utworzony i przypisany nowy href:', newHref);
    }

    console.log('[ContentBlockService->createOrUpdateHref] Zakończenie zarządzania href - finalny stan href:', block.href);
  }

  /**
   * Pobiera tablicę slajdów BlockSlider z bloku slidera o podanym identyfikatorze
   * @param blockId - Unikalny identyfikator bloku slidera
   * @returns Tablica obiektów BlockSlider lub pusta tablica jeśli blok nie istnieje lub nie zawiera slajdów
   * @remarks Przeszukuje strukturę _working i konwertuje dzieci kontenera slajdów na format BlockSlider
   */
  getSliders(blockId: string): BlockSlider[] {
    console.log('[ContentBlockService->getSliders] Rozpoczęcie pobierania slajdów dla bloku:', blockId);

    try {
      // Pobranie głównego bloku slidera
      const sliderBlock = this.getSliderBlock(blockId);
      if (!sliderBlock || !sliderBlock.special_block) {
        console.warn('[ContentBlockService->getSliders] Nie znaleziono bloku slidera o ID:', blockId);
        return [];
      }

      console.log('[ContentBlockService->getSliders] Znaleziony blok slidera:', sliderBlock.u_id);

      // Pobranie kontenera ze slajdami
      const slidesContainer = this.findSlidesContainer(sliderBlock.special_block.structure);
      if (!slidesContainer) {
        console.warn('[ContentBlockService->getSliders] Nie znaleziono kontenera slajdów w strukturze slidera');
        return [];
      }

      console.log('[ContentBlockService->getSliders] Znaleziony kontener slajdów:', slidesContainer.u_id);
      console.log('[ContentBlockService->getSliders] Liczba dzieci kontenera:', slidesContainer.children?.length || 0);

      // Konwersja dzieci kontenera na format BlockSlider
      const sliders: BlockSlider[] = [];

      if (slidesContainer.children && Array.isArray(slidesContainer.children)) {
        slidesContainer.children.forEach((child, index) => {
          console.log(`[ContentBlockService->getSliders] Przetwarzanie slajdu ${index + 1}:`, child.u_id);

          // Walidacja struktury dziecka
          if (!child.u_id) {
            console.warn(`[ContentBlockService->getSliders] Slajd ${index + 1} nie ma u_id, pomijanie`);
            return;
          }

          // Ekstrakcja stylów z bloku
          const styles = child.styles?.all?.basic || {};
          const backgroundColor = styles.background_color || '';
          const backgroundImage = styles.background_image || '';
          const isActive = child.is_active;

          console.log(`[ContentBlockService->getSliders] Dane slajdu ${child.u_id}:`, {
            backgroundColor,
            backgroundImage,
            isActive
          });

          // Utworzenie obiektu BlockSlider
          const slider: BlockSlider = {
            uId: child.u_id,
            backgroundColor: backgroundColor,
            backgroundImage: backgroundImage,
            isActive: isActive
          };

          sliders.push(slider);
        });

        // Sortowanie według custom_order dla zachowania kolejności
        sliders.sort((a, b) => {
          const childA = slidesContainer.children.find(child => child.u_id === a.uId);
          const childB = slidesContainer.children.find(child => child.u_id === b.uId);
          return (childA?.custom_order || 0) - (childB?.custom_order || 0);
        });
      }

      console.log('[ContentBlockService->getSliders] Finalna tablica slajdów:', sliders);
      console.log(`[ContentBlockService->getSliders] Zwracam ${sliders.length} slajdów dla bloku ${blockId}`);

      return sliders;

    } catch (error) {
      console.error('[ContentBlockService->getSliders] Krytyczny błąd podczas pobierania slajdów:', error);
      return [];
    }
  }

}
